import { Injectable, NgZone, inject } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import type { PoiInterface } from '@navvis/ivion';
import { PoiSelectedEventData } from '../PoiSelectedEventData';
import { MiddlewareApiService } from './api/middleware-api.service';
import { FmsService } from './api/fms.service';
import { IvionNavigationService } from './api/ivion-navigation.service';
import { SiteService } from './site-selector/site.service';

@Injectable({ providedIn: 'root' })
export class PoiDetailService {
	private ngZone = inject(NgZone);
	private api = inject(MiddlewareApiService);
	private fmsService = inject(FmsService);
	private ivionNavigation = inject(IvionNavigationService);
	private siteService = inject(SiteService);

	private poiSelectedSubject = new Subject<PoiSelectedEventData>();

	readonly poiSelected$ = this.poiSelectedSubject.asObservable();

	emitPoiSelected(data: PoiSelectedEventData): void {
		// Ensure event is processed inside Angular zone for immediate change detection
		this.ngZone.run(() => {
			this.poiSelectedSubject.next(data);
		});
	}

	/**
	 * Selects a POI by id from outside the map (search, defect overview):
	 * navigates the view to it, highlights it and opens the info panel with the
	 * same data a click on the POI in IVION would produce.
	 * Returns the resolved POI, or null when it does not exist.
	 */
	async selectPoi(poiId: number): Promise<PoiInterface | null> {
		const poi = await this.ivionNavigation.navigateToPoiId(poiId);
		if (!poi) {
			return null;
		}

		const data = new PoiSelectedEventData();
		data.id = poi.id;
		data.poiTypeId = poi.poiType?.id ?? 0;
		data.titles = poi.titles;
		data.descriptions = poi.descriptions;
		data.globalLocation = poi.location;
		data.icon = poi.icon;
		this.emitPoiSelected(data);
		return poi;
	}

	/**
	 * Selects a building coming from search or the FMS tree: opens the info
	 * panel and, when the building has a linked POI, navigates the map to it.
	 * The info panel is shown even without a POI (unscanned buildings), same
	 * as the legacy app.
	 */
	async selectBuilding(siteId: number, buildingId: number): Promise<void> {
		const building = await this.fmsService.getBuilding(siteId, buildingId);

		const data = new PoiSelectedEventData();
		data.poiTypeId = this.siteService.selectedSite?.settings?.fmsBuildingPoiTypeId ?? 0;
		data.fmsRef = { type: 'building', id: buildingId };
		if (building) {
			data.titles = { en: building.name, cs: building.name, de: building.name };
			data.descriptions = { en: building.description ?? '', cs: building.description ?? '', de: building.description ?? '' };
		}

		try {
			const result = await firstValueFrom(this.api.buildingToPoi(siteId, buildingId));
			if (result.found) {
				data.id = result.id;
				const poi = await this.ivionNavigation.navigateToPoiId(result.id);
				if (poi?.location) {
					data.globalLocation = poi.location;
				}
			} else {
				console.warn('[PoiDetail] Building POI not found:', buildingId);
			}
		} catch (err) {
			console.error('[PoiDetail] Error getting building POI:', err);
		}

		this.emitPoiSelected(data);
	}

	/**
	 * Selects a functional area coming from search or the FMS tree. Same
	 * behavior as {@link selectBuilding}.
	 */
	async selectFunctionalArea(siteId: number, faId: number): Promise<void> {
		const fa = await this.fmsService.getFunctionalAreaDetail(siteId, faId);

		const data = new PoiSelectedEventData();
		data.poiTypeId = this.siteService.selectedSite?.settings?.fmsFunctionalAreaPoiTypeId ?? 0;
		data.fmsRef = { type: 'fa', id: faId };
		if (fa) {
			data.titles = { en: fa.name, cs: fa.name, de: fa.name };
			data.descriptions = { en: fa.purpose ?? '', cs: fa.purpose ?? '', de: fa.purpose ?? '' };
		}

		try {
			const result = await firstValueFrom(this.api.functionalAreaToPoi(siteId, faId));
			if (result.found) {
				data.id = result.id;
				const poi = await this.ivionNavigation.navigateToPoiId(result.id);
				if (poi?.location) {
					data.globalLocation = poi.location;
				}
			} else {
				console.warn('[PoiDetail] FunctionalArea POI not found:', faId);
			}
		} catch (err) {
			console.error('[PoiDetail] Error getting FA POI:', err);
		}

		this.emitPoiSelected(data);
	}
}
